import { useId } from 'react';
import { Radio, RadioGroup } from '../../components/Radio';
import type { PlanTierOffer } from '../../types/planTiers';
import { formatAnnualPrice, threeYearPriceLine } from './signupFormat';

/**
 * One card per plan the lookup offered for the chosen sport, each a real radio. The card shows the plan's
 * name and yearly price as the radio's name, and its description and what it includes as the
 * radio's description, so a screen reader hears "Tier 2, $4,500 / year" then the details.
 *
 * Names, prices and what each plan includes all come from the server: admins edit them.
 */
const PlanCard = ({
  plan,
  currency,
  threeYearOfferEndsOn,
  checked,
  onChange,
  disabled,
  errorId,
}: {
  plan: PlanTierOffer;
  currency: string;
  threeYearOfferEndsOn: string | null;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  /** The group's error, when there is one, so each radio reads as invalid and describes it. */
  errorId?: string;
}) => {
  const detailsId = useId();
  const threeYearLine = threeYearPriceLine(plan.threeYearAnnualPriceCents, currency, threeYearOfferEndsOn);
  return (
    <div className={`signup-plan${checked ? ' signup-plan--checked' : ''}`}>
      <Radio
        name="planTier"
        value={plan.key}
        className="signup-plan__radio"
        label={
          <span className="signup-plan__title">
            <span className="signup-plan__name">{plan.name}</span>
            <span className="signup-plan__price">{formatAnnualPrice(plan.annualPriceCents, currency)}</span>
          </span>
        }
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-describedby={errorId ? `${detailsId} ${errorId}` : detailsId}
        aria-invalid={errorId ? true : undefined}
        data-testid={`signup-plan-${plan.key}`}
      />
      <div className="signup-plan__details" id={detailsId}>
        {plan.description && <p className="signup-plan__description">{plan.description}</p>}
        {threeYearLine && (
          <p className="signup-plan__description" data-testid={`signup-plan-three-year-${plan.key}`}>
            {threeYearLine}
          </p>
        )}
        <p className="signup-plan__includes-label">Includes</p>
        <ul className="signup-plan__includes">
          <li>Everything in the core app</li>
          {plan.features.map((feature) => (
            <li key={feature.key}>{feature.name}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export const SignupPlanChoice = ({
  plans,
  currency,
  threeYearOfferEndsOn = null,
  value,
  onChange,
  disabled,
  error,
}: {
  plans: PlanTierOffer[];
  currency: string;
  /** The last day (yyyy-MM-dd) to sign a 3-year agreement, added to each 3-year line. */
  threeYearOfferEndsOn?: string | null;
  value: string | undefined;
  onChange: (planKey: string) => void;
  disabled?: boolean;
  /** Shown under the cards, e.g. when the form was sent without a plan. */
  error?: string;
}) => {
  const errorId = useId();
  return (
    <div className="signup-plans">
      <RadioGroup label="Plan" testId="signup-plans">
        {plans.map((plan) => (
          <PlanCard
            key={plan.key}
            plan={plan}
            currency={currency}
            threeYearOfferEndsOn={threeYearOfferEndsOn}
            checked={value === plan.key}
            onChange={() => onChange(plan.key)}
            disabled={disabled}
            errorId={error ? errorId : undefined}
          />
        ))}
      </RadioGroup>
      {error && (
        <p className="signup-field-error" role="alert" id={errorId} data-testid="signup-plan-required">
          {error}
        </p>
      )}
    </div>
  );
};
